import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../supabase";
import ExpertSubscribeModal from "../components/ExpertSubscribeModal";

export default function ChoirMusicBooks() {
  const [musicBooks, setMusicBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userTier, setUserTier] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "title", asc: true });

  // Fetch user profile
  useEffect(() => {
    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("subscription_tier, subscription_expires_at")
          .eq("id", session.user.id)
          .single();
        const now = new Date();
        if (
          profile?.subscription_tier === "choir_expert" &&
          profile?.subscription_expires_at &&
          new Date(profile.subscription_expires_at) > now
        ) {
          setUserTier("choir_expert");
        } else {
          setUserTier(profile?.subscription_tier || "");
        }
      } else {
        setUserTier("");
      }
    };
    fetchUser();
  }, []);

  // Fetch music books
  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("music_books")
        .select("*")
        .eq("is_public", true)
        .order("id", { ascending: false });
      setMusicBooks(data || []);
      setLoading(false);
    };
    fetchBooks();
  }, []);

  // Modal handler
  const handleExpertAction = (e) => {
    e.preventDefault();
    setModalOpen(true);
  };

  // Search & Sorting
  const filteredBooks = useMemo(() => {
    let filtered = musicBooks.filter(
      (book) =>
        book.title?.toLowerCase().includes(search.toLowerCase()) ||
        book.author?.toLowerCase().includes(search.toLowerCase()) ||
        (book.tags && Array.isArray(book.tags)
          ? book.tags.join(" ").toLowerCase().includes(search.toLowerCase())
          : "")
    );
    filtered.sort((a, b) => {
      const aVal =
        (sort.key === "title"
          ? a.title
          : sort.key === "author"
          ? a.author
          : a.id) || "";
      const bVal =
        (sort.key === "title"
          ? b.title
          : sort.key === "author"
          ? b.author
          : b.id) || "";
      if (aVal < bVal) return sort.asc ? -1 : 1;
      if (aVal > bVal) return sort.asc ? 1 : -1;
      return 0;
    });
    return filtered;
  }, [musicBooks, search, sort]);

  return (
    <div className="min-h-[70vh] px-2 py-8 sm:py-10 max-w-screen-2xl mx-auto">
      <ExpertSubscribeModal open={modalOpen} onClose={() => setModalOpen(false)} />

      <div className="mb-7 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-green-800 text-center sm:text-left">
            Choir Music Books
          </h1>
          <p className="text-base md:text-lg text-green-700 max-w-xl text-center sm:text-left mt-1">
            Explore, search, and download digital choir music books. <b>Choir Expert</b> subscribers get unlimited downloads!
          </p>
        </div>
        {/* Search and Sort Bar */}
        <div className="flex gap-2 items-center w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by title, author, or tag..."
              className="w-full pl-10 pr-3 py-2 rounded-xl border border-green-300 focus:border-green-700 focus:ring-2 focus:ring-green-100 outline-none transition"
            />
            <span className="absolute left-3 top-2.5 h-5 w-5 text-green-400 pointer-events-none" role="img" aria-label="search">🔍</span>
          </div>
          <div className="flex items-center ml-2">
            <span className="text-green-700 mr-2 text-sm font-semibold">Sort by</span>
            <select
              value={sort.key}
              onChange={e => setSort(s => ({ ...s, key: e.target.value }))}
              className="border border-green-300 rounded-xl px-2 py-1 text-green-800 bg-white font-semibold"
            >
              <option value="title">Title</option>
              <option value="author">Author</option>
              <option value="id">Newest</option>
            </select>
            <button
              className="ml-2 p-1 rounded hover:bg-green-100"
              onClick={() => setSort(s => ({ ...s, asc: !s.asc }))}
              aria-label={sort.asc ? "Ascending" : "Descending"}
            >
              {sort.asc ? (
                <span className="w-5 h-5 text-green-600" role="img" aria-label="ascending">⬆️</span>
              ) : (
                <span className="w-5 h-5 text-green-600" role="img" aria-label="descending">⬇️</span>
              )}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-green-600 font-semibold animate-pulse">
          Loading music books...
        </div>
      ) : filteredBooks.length === 0 ? (
        <div className="text-center text-gray-400 py-16">No music books found.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 xl:gap-7">
          {filteredBooks.map((book) => (
            <div
              key={book.id}
              className="relative bg-white rounded-2xl shadow border border-green-100 aspect-[1/1.414] overflow-hidden group hover:shadow-2xl hover:-translate-y-1 duration-200 transition"
            >
              {/* Background Thumbnail (fills card) */}
              <img
                src={`https://drive.google.com/thumbnail?id=${book.google_drive_file_id}&sz=w600-h850`}
                alt={book.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
                draggable={false}
              />

              {/* Overlay for title/author/button */}
              <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 py-3 flex flex-col items-center z-10">
                {/* Title + author with dark bg */}
                <div className="w-full flex flex-col items-center mb-2">
                  <div className="bg-black/80 rounded-xl px-2 py-1 w-full">
                    <h2 className="text-xs sm:text-sm font-bold text-white text-center w-full line-clamp-2">
                      {book.title}
                    </h2>
                    <div className="text-green-100 font-medium text-[11px] w-full text-center line-clamp-2">
                      {book.author}
                    </div>
                  </div>
                </div>
                {/* Tags */}
                <div className="flex flex-wrap justify-center gap-1 mb-2">
                  {Array.isArray(book.tags)
                    ? book.tags.map((tag, i) => (
                        <span
                          key={i}
                          className="bg-green-200 text-green-800 px-1.5 py-0.5 rounded text-[9px] font-semibold"
                        >
                          {tag}
                        </span>
                      ))
                    : null}
                </div>
                {/* Download button */}
                {userTier === "choir_expert" ? (
                  <a
                    href={`https://drive.google.com/uc?export=download&id=${book.google_drive_file_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-gradient-to-r from-green-700 to-lime-500 text-white py-1 rounded-xl font-bold shadow hover:from-green-800 hover:to-lime-600 transition text-center text-xs block"
                  >
                    Download PDF
                  </a>
                ) : (
                  <button
                    onClick={handleExpertAction}
                    className="w-full bg-gradient-to-r from-green-700 to-lime-500 text-white py-1 rounded-xl font-bold shadow hover:from-green-800 hover:to-lime-600 transition text-center text-xs block"
                  >
                    Download PDF
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
